/**
 * CockpitPage — page shell that codifies the Cockpit Page Standard.
 *
 * Reference implementation: MarketingPerformanceCockpit (Lead Performance).
 * Use this for any analytical / dashboard surface rendered in cockpit skin.
 *
 * Anatomy:
 *   ┌────────────────────────────────────────────────────────────┐
 *   │  <h1>Title</h1>          [optional controls slot, right]   │  ← header
 *   ├────────────────────────────────────────────────────────────┤
 *   │  Tab · Tab · Tab · Tab                                     │  ← hairline tabs
 *   │  ─────────                                                 │
 *   │  [ Sub-pivot if present ]                                  │
 *   │                                                            │
 *   │  {children — KPI strip, table, etc.}                       │
 *   └────────────────────────────────────────────────────────────┘
 *
 * Rules (also enforced by memory `mem://style/cockpit-page-standard`):
 *   1. Exactly ONE date-range control per page (in `controls`).
 *   2. No echo of the active tab label as a sub-headline. Tabs speak for
 *      themselves; the sub-pivot below is the canonical secondary affordance.
 *   3. Active "toggled" state is always primary-tinted with a ring — never
 *      a plain background swap.
 *   4. URL is the source of truth for tab + pivot + range.
 */
import * as React from "react";
import { cn } from "@/lib/utils";

interface Props {
  title: string;
  /** Optional gray suffix rendered after the title on the same line (e.g. "· Attribution"). */
  titleSuffix?: string;
  /** Optional small uppercase eyebrow above the title (e.g. "Execution"). */
  eyebrow?: string;
  /** Optional one-line description rendered beneath the title. Keep terse. */
  subtitle?: React.ReactNode;
  /** Optional right-aligned slot for controls (typically <RangeToggle />). */
  controls?: React.ReactNode;
  /** Optional full-width slot rendered between the header divider and the
   *  tab strip. Use for shared elements that span both lenses (e.g. a KPI
   *  strip that's relevant regardless of which tab is active). */
  belowHeader?: React.ReactNode;
  /** Primary tab strip — typically <CockpitTabs />. */
  tabs?: React.ReactNode;
  /** Optional secondary segmented control beneath the tabs. */
  subPivot?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
  /** Max content width. Defaults to 6xl to match Lead Performance. */
  maxWidth?: "5xl" | "6xl" | "7xl" | "full";
}

const MAX_W = {
  "5xl": "max-w-5xl",
  "6xl": "max-w-6xl",
  "7xl": "max-w-7xl",
  full: "max-w-none",
} as const;

export function CockpitPage({
  title,
  titleSuffix,
  eyebrow,
  subtitle,
  controls,
  belowHeader,
  tabs,
  subPivot,
  children,
  className,
  maxWidth = "6xl",
}: Props) {
  return (
    <div className={cn(MAX_W[maxWidth], "mx-auto pb-12 pt-2", className)}>
      <header className="mb-3">
        <div className="flex items-start justify-between gap-4 flex-wrap pb-4 border-b border-border">
          <div>
            {eyebrow && (
              <p className="text-[10px] uppercase tracking-[0.08em] font-semibold text-muted-foreground/70">
                {eyebrow}
              </p>
            )}
            <h1 className={cn("kdr-display text-foreground", eyebrow && "mt-0.5")}>
              {titleSuffix}
              {/* {titleSuffix && <span className="text-muted-foreground font-medium"> · </span>} */}
            </h1>
            {subtitle && <p className="kdr-meta mt-1.5 max-w-xl">{subtitle}</p>}
          </div>
          {controls && <div className="flex items-center gap-3 shrink-0">{controls}</div>}
        </div>
        {belowHeader && <div className="mt-4">{belowHeader}</div>}
        {tabs && <div className={cn(belowHeader ? "mt-4" : "mt-6")}>{tabs}</div>}
      </header>
      {subPivot}
      {children}
    </div>
  );
}

export default CockpitPage;
